<script setup lang="ts">
import { provide } from "vue";
import { storeToRefs } from "pinia";
import { useCardDetails } from "./use-card-details";
import { Skill } from "fungi-types";
import SkillSwitchTabs from "~/components/card-details/SkillSwitchTabs.vue";
import OwnersHistorySection from "~/components/card-details/OwnersHistory/OwnersHistorySection.vue";
import AvailableCardsSection from "~/components/card-details/AvailableCards/AvailableCardsSection.vue";
import MarketplaceInfoSection from "~/components/card-details/MarketplaceInfo/MarketplaceInfoSection.vue";
import ScoresSection from "~/components/card-details/Scores/ScoresSection.vue";
import PlayerInfo from "~/components/card-details/PlayerInfo/PlayerInfo.vue";
import CardDetailsSkeleton from "./CardDetailsSkeleton.vue";
import AuctionItem from "~/components/marketplace/AuctionItem.vue";
import { useI18n } from "vue-i18n";
const { t } = useI18n();

const props = defineProps<{
  isModal?: boolean;
  hideBackground?: boolean;
}>();

const cardDetails = useCardDetails();
const { selectedCard, cardSales, selectedSkill } = storeToRefs(cardDetails);

const skillsGradients: Record<Skill, [string, string]> = {
  [Skill.SERVICE]: ["#A42A2700", "#A42A27CC"],
  [Skill.POWER]: ["#E39E1200", "#E39E12CC"],
  [Skill.RETURN]: ["#33AAFF00", "#33AAFFCC"],
  [Skill.MENTAL]: ["#53AA3300", "#53AA33CC"],
};
provide("skills-gradients", skillsGradients);

const skillsColors: Record<Skill, string> = {
  [Skill.SERVICE]: "#A42A27",
  [Skill.POWER]: "#E39E12",
  [Skill.RETURN]: "#33AAFF",
  [Skill.MENTAL]: "#53AA33",
};
provide("skills-colors", skillsColors);

const handleCloseCardDetails = () => {
  cardDetails.showModal = false;
};
</script>

<template>
  <CardDetailsSkeleton @close="handleCloseCardDetails">
    <template #aside>
      <div>
        <AuctionItem
          v-if="selectedCard"
          :showCardDetails="false"
          :item="selectedCard"
          :show-xp-bar="true"
        />
      </div>
    </template>
    <template #header>
      <div class="flex flex-col gap-4">
        <PlayerInfo class="mb-2 w-full" />
        <SkillSwitchTabs
          v-if="selectedSkill"
          class="self-stretch"
          :active-skill="selectedSkill"
          :skill-scores="cardDetails.l10Scores"
          @change="cardDetails.changeSkill"
        />
      </div>
    </template>
    <template #main>
      <div class="flex flex-col gap-6 py-6">
        <AuctionItem
          v-if="selectedCard?.skill === selectedSkill"
          class="block md:hidden w-full"
          :showCardDetails="false"
          :item="selectedCard"
          :show-xp-bar="true"
        />
        <ScoresSection />

        <MarketplaceInfoSection
          :title="t('cardDetails.history-sales.title')"
          :selectedCard="selectedCard"
          sales="history"
        />
        <MarketplaceInfoSection
          :title="t('cardDetails.ongoing-sales.title')"
          :selectedCard="selectedCard"
          sales="ongoing"
        />
      </div>
    </template>
  </CardDetailsSkeleton>
</template>

<style scoped></style>
